/*
 * The admin site's words in Danish: the articles section - line for line
 * articles.en.ts, which is the contract.
 */

import type { DeleteTargetKind } from '@accotool-kb/shared';
import type { articlesEn } from './articles.en.js';

/** 'release note' or 'article', as the publishing panel is given it - in the definite form. */
function definite(noun: string): string {
  return noun === 'release note' ? 'udgivelsesnoten' : 'artiklen';
}

/** "1 anden artikel", "2 andre artikler". */
function otherArticles(n: number): string {
  return `${n} ${n === 1 ? 'anden artikel' : 'andre artikler'}`;
}

function releaseNotes(n: number): string {
  return `${n} ${n === 1 ? 'udgivelsesnote' : 'udgivelsesnoter'}`;
}

function links(n: number): string {
  return `${n} ${n === 1 ? 'link' : 'links'}`;
}

const KIND: Record<DeleteTargetKind, string> = {
  article: 'artikel',
  'release-note': 'udgivelsesnote',
  product: 'produkt',
  category: 'kategori',
};

export const articlesDa: typeof articlesEn = {
  shared: {
    articles: 'Artikler',
    untitled: '(uden titel)',
    title: 'Titel',
    slug: 'Slug',
    categoryTags: 'Kategorier',
    properties: 'Egenskaber',
    publishing: 'Udgivelse',
    publishAtLocal: 'Udgivelsestidspunkt (din lokale tid)',
    saving: 'Gemmer…',
    saveAndPublish: 'Gem og udgiv',
    unsavedChanges: 'Ikke-gemte ændringer',
    cancel: 'Annuller',
    draft: 'kladde',
    live: 'live',
    titleRequired: 'Angiv en titel.',
    categoryRequired: 'Vælg mindst én kategori.',
  },

  list: {
    matching: (total) => `${total} ${total === 1 ? 'resultat' : 'resultater'}`,
    newArticle: 'Ny artikel',
    deleted: (label, steps) => `»${label}« er slettet. ${steps}`,
    search: 'Søg',
    searchPlaceholder: 'Titel, slug eller brødtekst…',
    state: 'Status',
    status: {
      all: 'Alle artikler',
      live: 'Live',
      scheduled: 'Planlagt',
      hidden: 'Skjult',
      draft: 'Ikke-udgivet kladde',
      withdrawn: 'Tekst trukket tilbage',
      unwritten: 'Endnu ikke skrevet',
      untagged: 'Uden kategori',
    },
    product: 'Produkt',
    category: 'Kategori',
    any: 'Alle',
    sort: 'Sortering',
    sortModified: 'Senest redigeret',
    sortCreated: 'Nyeste',
    sortPublish: 'Udgivelsesdato',
    sortTitle: 'Titel',
    loading: 'Indlæser artikler…',
    noneMatch: 'Ingen artikler matcher',
    noneMatchHint: 'Ret filtrene, eller opret den første artikel.',
    columnArticle: 'Artikel',
    columnState: 'Status',
    columnPublish: 'Udgivelse',
    columnEdited: 'Redigeret',
    columnActions: 'Handlinger',
    needsCategory: 'mangler en kategori',
    audienceNotConfigured: (key) => `${key} (ikke konfigureret)`,
    draftTitle: (latest, current) => `Revision ${latest} er nyere end den udgivne revision ${current ?? '—'}.`,
    edit: 'Rediger',
    delete: 'Slet',
  },

  create: {
    loadingMetadata: 'Indlæser metadata…',
    setUpMetadata: 'Opret metadata først',
    setUpMetadataBody:
      'En artikel skal have et produkt - det er halvdelen af adressen - og mindst én kategori. Nogle af dem findes ikke endnu.',
    noProductTags: 'Ingen produkter.',
    noCategoryTags: 'Ingen kategorier.',
    goToMetadata: 'Gå til Metadata',
    breadcrumbNew: 'Ny',
    heading: 'Ny artikel',
    intro: 'Artiklen oprettes med sin første revision, så den har noget at vise, så snart den udgives.',
    content: 'Indhold',
    slugRequired: 'Angiv en slug. Skriv selv en, hvis titlen ikke indeholder bogstaver eller cifre.',
    productRequired: 'Vælg et produkt.',
    slugHint:
      'Dannes ud fra titlen, indtil du redigerer den. Der tilføjes et tal, hvis det valgte produkt allerede bruger den.',
    whenVisible: 'Hvornår skal læserne kunne se den',
    modeHidden: 'Hold den skjult indtil videre',
    modeNow: 'Udgiv med det samme',
    modeSchedule: 'Planlæg til senere',
    hintHidden: 'Læserne kan intet se, før du udgiver den.',
    hintNow: 'Kan læses, så snart den er oprettet.',
    hintSchedule: 'Kan læses fra det valgte tidspunkt.',
    storedAs: (utc) => `Gemmes som ${utc}`,
    creating: 'Opretter…',
    createArticle: 'Opret artikel',
  },

  editor: {
    deleted: 'Slettet',
    deletedBody: (label) => `»${label}« og alt, hvad der hører til, er slettet.`,
    backToArticles: 'Tilbage til artikler',
    createdLine: 'Oprettet {created} · seneste aktivitet {modified}',
    viewOnSite: 'Vis på webstedet',
    deleteArticle: 'Slet artikel',
    savedAndPublished: (revision) => `Revision ${revision} er gemt og udgivet.`,
    savedAsDraft: (revision, readersSee) =>
      `Revision ${revision} er gemt som kladde. Læserne ser stadig revision ${readersSee ?? '—'}.`,
    outOfDate: 'Din kopi er forældet.',
    outOfDateHelp:
      'Kopiér din tekst til et sikkert sted, genindlæs siden, og foretag din ændring igen oven på den andens. Intet er blevet overskrevet.',
    text: 'Tekst',
    draftBadge: (revision) => `kladde #${revision ?? ''}`,
    nothingWritten: 'intet skrevet endnu',
    editingDraft: (latest, current) =>
      `Du redigerer kladden, revision ${latest ?? ''}; læserne får revision ${current ?? '—'}.`,
    editingDraftHelp: 'Brug Gem og udgiv for at gøre dette til den version, læserne ser.',
  },

  properties: {
    slugHint: 'Unik inden for produktet, ikke i hele vidensbasen: et andet produkt kan godt bruge samme slug.',
    slugTaken: 'Bruges allerede af en anden artikel i dette produkt. Vælg en anden slug.',
    addressChanges: 'Når du ændrer produktet eller sluggen, ændres adressen.',
    linksBeingChecked:
      'Det undersøges stadig, hvilke andre artikler der linker hertil via den nuværende adresse; de, der gør, omskrives til den nye, når du gemmer, hver som en ny revision.',
    linksWillBeRewritten: (articles) =>
      `${otherArticles(articles)} linker hertil via den nuværende adresse; de links omskrives til den nye, når du gemmer, hver som en ny revision.`,
    tokensKeepWorking: 'Links skrevet som {token}-tokens virker fortsat.',
    handWrittenLinks: 'Håndskrevne links til {address} bliver ikke opdateret.',
    save: 'Gem egenskaber',
    addressIsNow: (address) => `Adressen er nu ${address}.`,
    linksRenamed: (linkCount, articles, notes, revisions, previous) => {
      const holders =
        notes > 0
          ? articles > 0
            ? `${otherArticles(articles)} og ${releaseNotes(notes)}`
            : releaseNotes(notes)
          : otherArticles(articles);
      return (
        `${links(linkCount)} i ${holders} pegede på ${previous} og peger nu hertil ` +
        `(${revisions} ${revisions === 1 ? 'ny revision' : 'nye revisioner'} tilføjet).`
      );
    },
    noLinksRenamed: 'Ingen anden artikel linkede hertil via den gamle adresse.',
    draftLinksRenamed: (linkCount) =>
      `${links(linkCount)} til artiklens egen gamle adresse i teksten nedenfor blev også opdateret; ` +
      'gem teksten for at beholde ændringen.',
  },

  saveBox: {
    heading: 'Artikel',
    placeholder: 'f.eks. Opdaterede skærmbillederne til den nye portal',
    changeNote: 'Ændringsnote',
    saveAndPublishTitle: 'Tilføj en revision, og gør den til den, læserne får.',
    saveAsDraft: 'Gem som kladde',
    saveAsDraftTitle: 'Tilføj en revision uden at ændre, hvad læserne ser.',
  },

  publishing: {
    chooseDateTime: 'Vælg en dato og et klokkeslæt.',
    publishDate: 'Udgivelsestidspunkt: {date}',
    publishDateScheduled: 'Udgivelsestidspunkt: {date} — i fremtiden, så intet kan læses endnu.',
    unwritten: 'Sat til at blive udgivet, men der er ikke skrevet noget endnu.',
    unwrittenHelp: (noun) =>
      `Læserne ser intet, før en revision er udgivet. Skriv ${definite(noun)} nedenfor, og brug Gem og udgiv.`,
    publishNow: 'Udgiv med det samme',
    publishNowTitle: (noun) => `Sæt udgivelsesdatoen til nu, og gør ${definite(noun)} synlig.`,
    hide: 'Skjul for læserne',
    hideTitle: 'Skjul for alle undtagen administratorer. Kan fortrydes, og historikken bevares.',
    scheduleLegend: 'Planlægning',
    howStored: 'Sådan gemmes og vurderes udgivelsesdatoen',
    howStoredBody:
      'Gemmes som UTC. Planlægningen vurderes ud fra databasens ur, så den flytter sig ikke med sommertid.',
    alreadyPassed: (noun) =>
      `Det tidspunkt er allerede passeret, så ${definite(noun)} kan læses, så snart du gemmer.`,
    setPublishDate: 'Angiv udgivelsesdato',
    schedule: 'Planlæg',
  },

  history: {
    loading: 'Indlæser historik…',
    heading: 'Historik',
    count: (revisions) => `${revisions} ${revisions === 1 ? 'revision' : 'revisioner'}`,
    empty: 'Der er ikke skrevet noget endnu. Når du gemmer, oprettes revision 1.',
    characters: (characters) => `${characters.toLocaleString('da-DK')} tegn`,
    aheadOfLive: 'Nyere end den revision, læserne får.',
    view: 'Vis',
    compare: 'Sammenlign med live',
    promote: 'Udgiv denne',
    promoteTitle: 'Gør denne til den revision, læserne får. Træder i kraft med det samme.',
    restore: 'Kopiér til ny kladde',
    restoreTitle: 'Tilføj en kopi af denne revision som en ny kladde. Historikken bliver ikke omskrevet.',
    viewerTitle: (revision, title) => `Revision ${revision} — ${title}`,
    diffTitle: (live, other) => `Revision ${live} (live) sammenlignet med revision ${other}`,
    diffSummary: (added, removed, approximate) =>
      `${added} ${added === 1 ? 'linje' : 'linjer'} tilføjet, ${removed} fjernet${
        approximate ? ' (omtrentligt: revisionerne er meget store)' : ''
      }`,
    titleDiffers: 'Titlen er ændret: {from} → {to}',
  },

  visibility: {
    label: 'Synlighed',
    publicHint: 'Alle kan læse den, når den er udgivet.',
    privateHint:
      'Når den er udgivet, kan kun de målgrupper, der er sat flueben ved nedenfor, og administratorerne læse den - logget ind.',
    privateUnavailable: 'Der er ikke konfigureret nogen målgrupper på denne server, så den kan ikke gøres privat.',
    audiences: 'Målgrupper',
    loadingAudiences: 'Indlæser målgrupperne…',
    noAudiences:
      'Der er ikke konfigureret nogen målgrupper på denne server. Gør artiklen offentlig, eller bed om at få en konfigureret.',
    noLongerConfigured: (keys) => `Ikke længere konfigureret og fjernes, når du gemmer: ${keys}.`,
    audienceRequired: 'Vælg mindst én målgruppe, eller gør artiklen offentlig.',
  },

  tags: {
    product: 'Produkt',
    noTags: (kind) =>
      kind === 'product'
        ? 'Der findes endnu ingen produkter. Opret et under Metadata.'
        : 'Der findes endnu ingen kategorier. Opret en under Metadata.',
    retired: '(udfaset)',
  },

  linkedFrom: {
    heading: 'Linket fra',
    intro: (holders) =>
      `${
        holders === 'both'
          ? 'Disse artikler og udgivelsesnoter henviser'
          : holders === 'releaseNotes'
            ? 'Disse udgivelsesnoter henviser'
            : 'Disse artikler henviser'
      } til denne med {token}-tokens. Sletter du artiklen, bliver de omskrevet.`,
    articles: 'Artikler',
    releaseNotes: 'Udgivelsesnoter',
    revision: (revision, isCurrent) => `(revision ${revision}, ${isCurrent ? 'live' : 'kladde'})`,
  },

  deleteDialog: {
    title: (kind, name) => `Slet ${KIND[kind]}: ${name}`,
    effect: {
      cascade: 'databasekaskade',
      cleanup: 'ryddes op',
      rewrite: 'teksten omskrives',
      blocked: 'blokerer sletningen',
      none: 'til orientering',
    },
    nothingChangedYet: 'Intet er ændret endnu.',
    cannotDelete: 'Som tingene står, kan dette ikke slettes.',
    deleting: 'Sletter…',
    deletePermanently: 'Slet permanent',
    working: 'Undersøger, hvad det vil påvirke…',
    choose: 'Vælg…',
    whatWillHappen: 'Hvad der vil ske',
    typeToConfirm: 'Skriv {name} for at bekræfte',
    typeToConfirmHint: 'Sletningen fjerner versionshistorik, som ikke kan gendannes.',
    showItems: (shown, total) => `Vis ${shown}${total === null ? '' : ` af ${total}`}`,
    moreNotListed: (more) => `Yderligere ${more} vises ikke.`,
  },
};
